import React, { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { getErrorMessage } from '../../utils/api'
import { safeInitial } from '../../utils/safeString'
// WidgetNoticesSettings moved to dedicated /notices page

/* ─── Types ─────────────────────────────────────────────────────────────── */

interface AgentUser {
  id: string
  name: string
  email: string
  role: string
  is_active: boolean
  avatar_url?: string | null
  open_chats: number
  total_chats_today: number
}

interface NewUserForm {
  name: string
  email: string
  password: string
  role: string
}

const PERMISSIONS_MAP: Record<string, { label: string; description: string }> = {
  manage_users: { label: 'Manage Users', description: 'Create, edit, and deactivate user accounts' },
  manage_settings: { label: 'Manage Settings', description: 'Configure platform settings and integrations' },
  view_analytics: { label: 'View Analytics', description: 'Access analytics dashboards and reports' },
  manage_team: { label: 'Manage Team', description: 'Monitor team performance and assign chats' },
  handle_chats: { label: 'Handle Chats', description: 'Accept and respond to customer conversations' },
  use_ai: { label: 'Use AI Tools', description: 'Access Claude AI suggestions and copilot' },
}

const ROLE_COLOURS: Record<string, string> = {
  // Three-role model (29 Jul 2026)
  claude_dev: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  dev: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  sales_support: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  // Legacy values (until fully migrated)
  admin: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  supervisor: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  agent: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
}

/** Human-readable role names; falls back to the raw value so nothing ever renders blank. */
const ROLE_NAMES: Record<string, string> = {
  claude_dev: 'Claude Dev',
  dev: 'Dev',
  sales_support: 'Sales & Support',
  admin: 'Admin',
  supervisor: 'Supervisor',
  agent: 'Agent',
}
const roleName = (r: string) => ROLE_NAMES[r] || r || '—'

/* ─── SVG Icons ─────────────────────────────────────────────────────────── */

const PlusIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
)

const PencilIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
  </svg>
)

const ShieldIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
  </svg>
)

const XIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
)

const CheckIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
  </svg>
)

const BanIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
  </svg>
)

/* ─── Settings Page ─────────────────────────────────────────────────────── */

export const SettingsPage: React.FC<{
  /** Preselect a single tab (used when embedded as a Settings-hub section). */
  initialTab?: 'users' | 'permissions' | 'linking'
  /** Hide the internal tab bar (the hub provides its own section nav). */
  hideTabs?: boolean
}> = ({ initialTab = 'users', hideTabs = false }) => {
  const { currentAgent } = useAuth()
  // Three-role model (29 Jul 2026): every authenticated agent is admin-TIER
  // (the API's effectiveRole maps all roles to 'admin'); role names are labels.
  // Checking role === 'admin' would lock everyone out once roles become
  // claude_dev / dev / sales_support.
  const isAdmin = !!currentAgent

  const [agents, setAgents] = useState<AgentUser[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingAgent, setEditingAgent] = useState<AgentUser | null>(null)
  const [permissionsAgent, setPermissionsAgent] = useState<AgentUser | null>(null)
  const [permissions, setPermissions] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'users' | 'permissions' | 'linking'>(initialTab)

  // Follow the hub's section even if this instance is reused (belt & braces —
  // the hub also keys the section so this normally remounts fresh).
  useEffect(() => { setActiveTab(initialTab) }, [initialTab])

  const getToken = () => localStorage.getItem('auth_token') || ''

  const fetchAgents = useCallback(async () => {
    try {
      const res = await fetch('/api/agents', {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      if (!res.ok) throw new Error('Failed to fetch agents')
      const data = await res.json()
      setAgents(data.agents)
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load users'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAgents()
  }, [fetchAgents])

  const showNotification = (msg: string, type: 'success' | 'error') => {
    if (type === 'success') {
      setSuccess(msg)
      setError(null)
    } else {
      setError(msg)
      setSuccess(null)
    }
    setTimeout(() => {
      setSuccess(null)
      setError(null)
    }, 4000)
  }

  const handleCreateAgent = async (form: NewUserForm) => {
    try {
      const res = await fetch('/api/agents', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(form),
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(typeof data.error === 'string' ? data.error : (typeof data.message === 'string' ? data.message : 'Failed to create user'))
      }

      showNotification(`${form.name} has been added as ${form.role}`, 'success')
      setShowAddModal(false)
      await fetchAgents()
    } catch (err) {
      showNotification(getErrorMessage(err, 'Failed to create user'), 'error')
    }
  }

  const handleUpdateAgent = async (id: string, updates: Partial<AgentUser & { password?: string }>) => {
    try {
      const res = await fetch(`/api/agents/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(updates),
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(typeof data.error === 'string' ? data.error : (typeof data.message === 'string' ? data.message : 'Failed to update user'))
      }

      showNotification('User updated successfully', 'success')
      setEditingAgent(null)
      await fetchAgents()
    } catch (err) {
      showNotification(getErrorMessage(err, 'Failed to update user'), 'error')
    }
  }

  const handleDeactivate = async (agent: AgentUser) => {
    if (agent.id === currentAgent?.id) {
      showNotification('You cannot deactivate your own account', 'error')
      return
    }
    try {
      const res = await fetch(`/api/agents/${agent.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` },
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(typeof data.error === 'string' ? data.error : (typeof data.message === 'string' ? data.message : 'Failed to deactivate user'))
      }

      showNotification(`${agent.name} has been deactivated`, 'success')
      await fetchAgents()
    } catch (err) {
      showNotification(getErrorMessage(err, 'Failed to deactivate user'), 'error')
    }
  }

  const fetchPermissions = async (agentId: string) => {
    try {
      const res = await fetch(`/api/agents/${agentId}/permissions`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      if (!res.ok) throw new Error('Failed to fetch permissions')
      const data = await res.json()
      setPermissions(data.permissions)
    } catch {
      setPermissions([])
    }
  }

  const handleViewPermissions = (agent: AgentUser) => {
    setPermissionsAgent(agent)
    fetchPermissions(agent.id)
  }

  /* ─── Render ──────────────────────────────────────────────────────────── */

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-txt-primary">Settings</h1>
          <p className="text-sm text-txt-secondary mt-1">Manage users, roles, and platform settings</p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium transition-colors duration-150 shadow-sm"
          >
            <PlusIcon />
            Add User
          </button>
        )}
      </div>

      {/* Notifications */}
      {(error || success) && (
        <div className={`mb-6 px-4 py-3 rounded-lg text-sm font-medium animate-slide-down ${
          error
            ? 'bg-error-light text-error dark:bg-red-900/30 dark:text-red-300'
            : 'bg-success-light text-success dark:bg-green-900/30 dark:text-green-300'
        }`}>
          {error || success}
        </div>
      )}

      {/* Tabs */}
      {!hideTabs && (
      <div className="flex gap-1 mb-6 border-b border-border">
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors duration-150 -mb-px ${
            activeTab === 'users'
              ? 'border-brand-600 text-brand-600'
              : 'border-transparent text-txt-secondary hover:text-txt-primary'
          }`}
        >
          Users ({agents.length})
        </button>
        <button
          onClick={() => setActiveTab('permissions')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors duration-150 -mb-px ${
            activeTab === 'permissions'
              ? 'border-brand-600 text-brand-600'
              : 'border-transparent text-txt-secondary hover:text-txt-primary'
          }`}
        >
          Roles & Permissions
        </button>
        {isAdmin && (
          <button
            onClick={() => setActiveTab('linking')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors duration-150 -mb-px ${
              activeTab === 'linking'
                ? 'border-brand-600 text-brand-600'
                : 'border-transparent text-txt-secondary hover:text-txt-primary'
            }`}
          >
            Linking Thresholds
          </button>
        )}
      </div>
      )}

      {activeTab === 'users' && (
        <>
          {/* Users Table */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-6 h-6 border-2 border-brand-600 border-t-transparent rounded-full animate-spin-slow" />
            </div>
          ) : (
            <div className="bg-surface rounded-xl border border-border overflow-hidden shadow-sm">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-surface-secondary">
                    <th className="text-left px-5 py-3.5 text-xs font-semibold text-txt-secondary uppercase tracking-wider">User</th>
                    <th className="text-left px-5 py-3.5 text-xs font-semibold text-txt-secondary uppercase tracking-wider">Role</th>
                    <th className="text-left px-5 py-3.5 text-xs font-semibold text-txt-secondary uppercase tracking-wider">Status</th>
                    <th className="text-left px-5 py-3.5 text-xs font-semibold text-txt-secondary uppercase tracking-wider">Workload</th>
                    {isAdmin && (
                      <th className="text-right px-5 py-3.5 text-xs font-semibold text-txt-secondary uppercase tracking-wider">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {agents.map(agent => (
                    <tr
                      key={agent.id}
                      className="hover:bg-surface-hover transition-colors duration-100"
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          {agent.avatar_url ? (
                            <img
                              src={agent.avatar_url}
                              alt={agent.name}
                              className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                            />
                          ) : (
                            <div className="flex items-center justify-center w-9 h-9 rounded-full bg-brand-600 text-white text-sm font-semibold flex-shrink-0">
                              {safeInitial(agent.name)}
                            </div>
                          )}
                          <div>
                            <p className="text-sm font-medium text-txt-primary">{agent.name}</p>
                            <p className="text-xs text-txt-tertiary">{agent.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${ROLE_COLOURS[agent.role] || 'bg-surface-secondary text-txt-secondary'}`}>
                          {roleName(agent.role)}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${
                          agent.is_active
                            ? 'text-success'
                            : 'text-txt-tertiary'
                        }`}>
                          <span className={`w-2 h-2 rounded-full ${
                            agent.is_active ? 'bg-success' : 'bg-txt-tertiary'
                          }`} />
                          {agent.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-sm text-txt-secondary">{agent.open_chats} open chats</p>
                      </td>
                      {isAdmin && (
                        <td className="px-5 py-4">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => handleViewPermissions(agent)}
                              className="p-2 rounded-lg text-txt-tertiary hover:text-brand-600 hover:bg-surface-hover transition-colors duration-150"
                              title="View permissions"
                            >
                              <ShieldIcon />
                            </button>
                            <button
                              onClick={() => setEditingAgent(agent)}
                              className="p-2 rounded-lg text-txt-tertiary hover:text-brand-600 hover:bg-surface-hover transition-colors duration-150"
                              title="Edit user"
                            >
                              <PencilIcon />
                            </button>
                            {agent.id !== currentAgent?.id && agent.is_active && (
                              <button
                                onClick={() => handleDeactivate(agent)}
                                className="p-2 rounded-lg text-txt-tertiary hover:text-error hover:bg-error-light transition-colors duration-150"
                                title="Deactivate user"
                              >
                                <BanIcon />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {activeTab === 'permissions' && <PermissionsMatrix />}

      {activeTab === 'linking' && <LinkingThresholdsTab />}

      {/* Add User Modal */}
      {showAddModal && (
        <AddUserModal
          onClose={() => setShowAddModal(false)}
          onCreate={handleCreateAgent}
        />
      )}

      {/* Edit User Modal */}
      {editingAgent && (
        <EditUserModal
          agent={editingAgent}
          onClose={() => setEditingAgent(null)}
          onSave={handleUpdateAgent}
          currentAgentId={currentAgent?.id || ''}
        />
      )}

      {/* Permissions Modal */}
      {permissionsAgent && (
        <PermissionsModal
          agent={permissionsAgent}
          permissions={permissions}
          onClose={() => setPermissionsAgent(null)}
        />
      )}
    </div>
  )
}

/* ─── Permissions Matrix (Tab Content) ──────────────────────────────────── */

const PermissionsMatrix: React.FC = () => {
  // Three-role model (29 Jul 2026): every role is admin-tier for the platform
  // permissions; the ONE real difference is CRM access (Dev accounts are
  // blocked from the CRM and land in Dev Mode instead).
  const roles = ['claude_dev', 'dev', 'sales_support']
  const allPerms = [...Object.keys(PERMISSIONS_MAP), 'access_crm']
  const permMeta: Record<string, { label: string; description: string }> = {
    ...PERMISSIONS_MAP,
    access_crm: { label: 'Access CRM', description: 'Deals, pipeline, and CRM tools (Dev accounts land in Dev Mode instead)' },
  }

  const rolePermissions: Record<string, string[]> = {
    claude_dev: ['manage_users', 'manage_settings', 'view_analytics', 'manage_team', 'handle_chats', 'use_ai', 'access_crm'],
    dev: ['manage_users', 'manage_settings', 'view_analytics', 'manage_team', 'handle_chats', 'use_ai'],
    sales_support: ['manage_users', 'manage_settings', 'view_analytics', 'manage_team', 'handle_chats', 'use_ai', 'access_crm'],
  }

  return (
    <div className="bg-surface rounded-xl border border-border overflow-hidden shadow-sm">
      <div className="px-5 py-4 border-b border-border">
        <h3 className="text-sm font-semibold text-txt-primary">Role Permissions Matrix</h3>
        <p className="text-xs text-txt-tertiary mt-1">Overview of what each role can access</p>
      </div>
      <table className="w-full">
        <thead>
          <tr className="border-b border-border bg-surface-secondary">
            <th className="text-left px-5 py-3 text-xs font-semibold text-txt-secondary uppercase tracking-wider">Permission</th>
            {roles.map(role => (
              <th key={role} className="text-center px-5 py-3 text-xs font-semibold text-txt-secondary uppercase tracking-wider">
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${ROLE_COLOURS[role]}`}>
                  {roleName(role)}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {allPerms.map(perm => (
            <tr key={perm} className="hover:bg-surface-hover transition-colors duration-100">
              <td className="px-5 py-3.5">
                <p className="text-sm font-medium text-txt-primary">{permMeta[perm].label}</p>
                <p className="text-xs text-txt-tertiary">{permMeta[perm].description}</p>
              </td>
              {roles.map(role => (
                <td key={role} className="text-center px-5 py-3.5">
                  {rolePermissions[role].includes(perm) ? (
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-success/10 text-success">
                      <CheckIcon />
                    </span>
                  ) : (
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-surface-tertiary text-txt-tertiary">
                      <span className="w-1 h-1 rounded-full bg-current" />
                    </span>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/* ─── Linking Thresholds Tab ──────────────────────────────────────────────── */

interface LinkingThresholds {
  whatsapp_name_match_threshold: number
  zoho_name_match_threshold: number
  zoho_email_exact_confidence: number
  zoho_fuzzy_reduction_factor: number
  zoho_minimum_confidence: number
  widget_email_confidence: number
}

const LinkingThresholdsTab: React.FC = () => {
  const [thresholds, setThresholds] = useState<LinkingThresholds>({
    whatsapp_name_match_threshold: 0.5,
    zoho_name_match_threshold: 0.5,
    zoho_email_exact_confidence: 0.95,
    zoho_fuzzy_reduction_factor: 0.8,
    zoho_minimum_confidence: 0.4,
    widget_email_confidence: 0.9,
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const getToken = () => localStorage.getItem('auth_token') || ''

  useEffect(() => {
    fetchThresholds()
  }, [])

  const fetchThresholds = async () => {
    setLoading(true)
    try {
      const res = await fetch('/api/settings/linking-thresholds', {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      if (!res.ok) throw new Error('Failed to fetch thresholds')
      const data = await res.json()
      setThresholds(data)
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load thresholds'))
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const res = await fetch('/api/settings/linking-thresholds', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(thresholds),
      })
      if (!res.ok) throw new Error('Failed to save thresholds')
      setSuccess('Linking thresholds saved successfully')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to save thresholds'))
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    if (!confirm('Reset all thresholds to defaults?')) return
    setThresholds({
      whatsapp_name_match_threshold: 0.5,
      zoho_name_match_threshold: 0.5,
      zoho_email_exact_confidence: 0.95,
      zoho_fuzzy_reduction_factor: 0.8,
      zoho_minimum_confidence: 0.4,
      widget_email_confidence: 0.9,
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="max-w-2xl">
      {error && (
        <div className="mb-4 px-4 py-3 bg-error-light text-error rounded-lg text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 px-4 py-3 bg-success-light text-success rounded-lg text-sm">
          {success}
        </div>
      )}

      <div className="bg-surface rounded-xl border border-border overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h3 className="text-sm font-semibold text-txt-primary">Linking Configuration</h3>
          <p className="text-xs text-txt-tertiary mt-1">Configure confidence thresholds for customer linking</p>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 gap-4">
            {/* WhatsApp Name Match Threshold */}
            <div>
              <label className="block text-sm font-medium text-txt-primary mb-2">
                WhatsApp Name Match Threshold
              </label>
              <input
                type="number"
                min="0"
                max="1"
                step="0.05"
                value={thresholds.whatsapp_name_match_threshold}
                onChange={e =>
                  setThresholds({
                    ...thresholds,
                    whatsapp_name_match_threshold: parseFloat(e.target.value) || 0,
                  })
                }
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-600"
              />
              <p className="text-xs text-txt-tertiary mt-1">Minimum similarity score (0-1) for WhatsApp group names</p>
            </div>

            {/* Zoho Name Match Threshold */}
            <div>
              <label className="block text-sm font-medium text-txt-primary mb-2">
                Zoho Name Match Threshold
              </label>
              <input
                type="number"
                min="0"
                max="1"
                step="0.05"
                value={thresholds.zoho_name_match_threshold}
                onChange={e =>
                  setThresholds({
                    ...thresholds,
                    zoho_name_match_threshold: parseFloat(e.target.value) || 0,
                  })
                }
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-600"
              />
              <p className="text-xs text-txt-tertiary mt-1">Minimum similarity score for Zoho chat names</p>
            </div>

            {/* Zoho Email Exact Confidence */}
            <div>
              <label className="block text-sm font-medium text-txt-primary mb-2">
                Zoho Email Exact Confidence
              </label>
              <input
                type="number"
                min="0"
                max="1"
                step="0.05"
                value={thresholds.zoho_email_exact_confidence}
                onChange={e =>
                  setThresholds({
                    ...thresholds,
                    zoho_email_exact_confidence: parseFloat(e.target.value) || 0,
                  })
                }
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-600"
              />
              <p className="text-xs text-txt-tertiary mt-1">Confidence score required for exact email matches</p>
            </div>

            {/* Zoho Fuzzy Reduction Factor */}
            <div>
              <label className="block text-sm font-medium text-txt-primary mb-2">
                Zoho Fuzzy Reduction Factor
              </label>
              <input
                type="number"
                min="0"
                max="1"
                step="0.05"
                value={thresholds.zoho_fuzzy_reduction_factor}
                onChange={e =>
                  setThresholds({
                    ...thresholds,
                    zoho_fuzzy_reduction_factor: parseFloat(e.target.value) || 0,
                  })
                }
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-600"
              />
              <p className="text-xs text-txt-tertiary mt-1">Reduction factor for fuzzy email matches (0-1)</p>
            </div>

            {/* Zoho Minimum Confidence */}
            <div>
              <label className="block text-sm font-medium text-txt-primary mb-2">
                Zoho Minimum Confidence
              </label>
              <input
                type="number"
                min="0"
                max="1"
                step="0.05"
                value={thresholds.zoho_minimum_confidence}
                onChange={e =>
                  setThresholds({
                    ...thresholds,
                    zoho_minimum_confidence: parseFloat(e.target.value) || 0,
                  })
                }
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-600"
              />
              <p className="text-xs text-txt-tertiary mt-1">Minimum confidence score for any Zoho match</p>
            </div>

            {/* Widget Email Confidence */}
            <div>
              <label className="block text-sm font-medium text-txt-primary mb-2">
                Widget Email Confidence
              </label>
              <input
                type="number"
                min="0"
                max="1"
                step="0.05"
                value={thresholds.widget_email_confidence}
                onChange={e =>
                  setThresholds({
                    ...thresholds,
                    widget_email_confidence: parseFloat(e.target.value) || 0,
                  })
                }
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-600"
              />
              <p className="text-xs text-txt-tertiary mt-1">Confidence score required for widget email matches</p>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-border bg-surface-secondary flex justify-between">
          <button
            onClick={handleReset}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-txt-secondary border border-border rounded-lg hover:bg-surface-hover transition-colors disabled:opacity-50"
          >
            Reset to Defaults
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ─── Add User Modal ────────────────────────────────────────────────────── */

interface AddUserModalProps {
  onClose: () => void
  onCreate: (form: NewUserForm) => Promise<void>
}

const AddUserModal: React.FC<AddUserModalProps> = ({ onClose, onCreate }) => {
  const [form, setForm] = useState<NewUserForm>({
    name: '',
    email: '',
    password: '',
    role: 'sales_support',
  })
  const [saving, setSaving] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    await onCreate(form)
    setSaving(false)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-fade-in">
      <div className="bg-surface rounded-2xl shadow-float border border-border w-full max-w-md mx-4 animate-slide-up">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-txt-primary">Add New User</h2>
          <button onClick={onClose} className="p-1 rounded-lg text-txt-tertiary hover:text-txt-primary hover:bg-surface-hover transition-colors">
            <XIcon />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-txt-secondary mb-1.5">Full Name</label>
            <input
              type="text"
              required
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              className="w-full px-3.5 py-2.5 rounded-lg border border-border bg-surface text-txt-primary text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 transition-colors"
              placeholder="e.g. Sarah Connor"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-txt-secondary mb-1.5">Email Address</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              className="w-full px-3.5 py-2.5 rounded-lg border border-border bg-surface text-txt-primary text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 transition-colors"
              placeholder="e.g. sarah@wetech.cloud"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-txt-secondary mb-1.5">Password</label>
            <input
              type="password"
              required
              minLength={6}
              value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}
              className="w-full px-3.5 py-2.5 rounded-lg border border-border bg-surface text-txt-primary text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 transition-colors"
              placeholder="Minimum 6 characters"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-txt-secondary mb-1.5">Role</label>
            <select
              value={form.role}
              onChange={e => setForm({ ...form, role: e.target.value as NewUserForm['role'] })}
              className="w-full px-3.5 py-2.5 rounded-lg border border-border bg-surface text-txt-primary text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 transition-colors"
            >
              <option value="sales_support">Sales &amp; Support — support + CRM</option>
              <option value="dev">Dev — lands in Dev Mode on login</option>
              <option value="claude_dev">Claude Dev — everything</option>
            </select>
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-lg text-sm font-medium text-txt-secondary hover:bg-surface-hover border border-border transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 shadow-sm"
            >
              {saving ? (
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin-slow" />
              ) : (
                <PlusIcon />
              )}
              {saving ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

/* ─── Edit User Modal ───────────────────────────────────────────────────── */

interface EditUserModalProps {
  agent: AgentUser
  onClose: () => void
  onSave: (id: string, updates: Partial<AgentUser & { password?: string }>) => Promise<void>
  currentAgentId: string
}

const CameraIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" />
  </svg>
)

const EditUserModal: React.FC<EditUserModalProps> = ({ agent, onClose, onSave, currentAgentId }) => {
  const [name, setName] = useState(agent.name)
  const [email, setEmail] = useState(agent.email)
  const [role, setRole] = useState(agent.role)
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [saving, setSaving] = useState(false)
  const [avatarUrl, setAvatarUrl] = useState<string | null>(agent.avatar_url || null)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const [avatarError, setAvatarError] = useState<string | null>(null)
  const avatarInputRef = React.useRef<HTMLInputElement>(null)

  const isSelf = agent.id === currentAgentId

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      setAvatarError('Please select an image file')
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      setAvatarError('Image must be under 5MB')
      return
    }

    setUploadingAvatar(true)
    setAvatarError(null)

    try {
      const formData = new FormData()
      formData.append('file', file)

      const token = localStorage.getItem('auth_token') || ''
      const res = await fetch(`/api/agents/${agent.id}/avatar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(typeof data.error === 'string' ? data.error : (typeof data.message === 'string' ? data.message : 'Upload failed'))
      }

      const data = await res.json()
      setAvatarUrl(data.avatar_url)
    } catch (err) {
      setAvatarError(getErrorMessage(err, 'Upload failed'))
    } finally {
      setUploadingAvatar(false)
      // Reset the input so the same file can be re-selected
      if (avatarInputRef.current) avatarInputRef.current.value = ''
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    const updates: Record<string, any> = { name, email }
    if (!isSelf) updates.role = role
    if (password) updates.password = password
    await onSave(agent.id, updates)
    setSaving(false)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-fade-in">
      <div className="bg-surface rounded-2xl shadow-float border border-border w-full max-w-md mx-4 animate-slide-up">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-txt-primary">Edit User</h2>
          <button onClick={onClose} className="p-1 rounded-lg text-txt-tertiary hover:text-txt-primary hover:bg-surface-hover transition-colors">
            <XIcon />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Avatar Upload */}
          <div className="flex flex-col items-center gap-2 pb-2">
            <div
              onClick={() => avatarInputRef.current?.click()}
              className="relative w-20 h-20 rounded-full cursor-pointer group"
            >
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={name}
                  className="w-20 h-20 rounded-full object-cover"
                />
              ) : (
                <div className="flex items-center justify-center w-20 h-20 rounded-full bg-brand-600 text-white text-2xl font-semibold">
                  {safeInitial(name)}
                </div>
              )}
              <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                {uploadingAvatar ? (
                  <span className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <span className="text-white"><CameraIcon /></span>
                )}
              </div>
            </div>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarUpload}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => avatarInputRef.current?.click()}
              className="text-xs text-brand-600 hover:text-brand-700 font-medium transition-colors"
            >
              {uploadingAvatar ? 'Uploading...' : 'Change photo'}
            </button>
            {avatarError && (
              <p className="text-xs text-error">{avatarError}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-txt-secondary mb-1.5">Full Name</label>
            <input
              type="text"
              required
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-lg border border-border bg-surface text-txt-primary text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 transition-colors"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-txt-secondary mb-1.5">Email Address</label>
            <input
              type="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-lg border border-border bg-surface text-txt-primary text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 transition-colors"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-txt-secondary mb-1.5">Role</label>
            <select
              value={role}
              onChange={e => setRole(e.target.value as AgentUser['role'])}
              disabled={isSelf}
              className="w-full px-3.5 py-2.5 rounded-lg border border-border bg-surface text-txt-primary text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="sales_support">Sales &amp; Support</option>
              <option value="dev">Dev</option>
              <option value="claude_dev">Claude Dev</option>
            </select>
            {isSelf && (
              <p className="text-xs text-txt-tertiary mt-1">You cannot change your own role</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-txt-secondary mb-1.5">New Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                minLength={6}
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full px-3.5 py-2.5 pr-20 rounded-lg border border-border bg-surface text-txt-primary text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 transition-colors"
                placeholder="Leave blank to keep current"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 rounded text-xs font-medium text-txt-tertiary hover:text-txt-primary hover:bg-surface-hover transition-colors"
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-lg text-sm font-medium text-txt-secondary hover:bg-surface-hover border border-border transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 shadow-sm"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

/* ─── Permissions Modal ─────────────────────────────────────────────────── */

interface PermissionsModalProps {
  agent: AgentUser
  permissions: string[]
  onClose: () => void
}

const PermissionsModal: React.FC<PermissionsModalProps> = ({ agent, permissions, onClose }) => {
  const allPerms = Object.keys(PERMISSIONS_MAP)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-fade-in">
      <div className="bg-surface rounded-2xl shadow-float border border-border w-full max-w-md mx-4 animate-slide-up">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold text-txt-primary">{agent.name}</h2>
            <p className="text-xs text-txt-tertiary mt-0.5">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${ROLE_COLOURS[agent.role] || 'bg-surface-secondary text-txt-secondary'}`}>
                {roleName(agent.role)}
              </span>
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg text-txt-tertiary hover:text-txt-primary hover:bg-surface-hover transition-colors">
            <XIcon />
          </button>
        </div>
        <div className="p-6 space-y-3">
          {allPerms.map(perm => {
            const has = permissions.includes(perm)
            return (
              <div
                key={perm}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg border transition-colors ${
                  has
                    ? 'border-success/20 bg-success/5'
                    : 'border-border bg-surface-secondary opacity-50'
                }`}
              >
                <span className={`flex items-center justify-center w-5 h-5 rounded-full ${
                  has ? 'bg-success text-white' : 'bg-surface-tertiary text-txt-tertiary'
                }`}>
                  {has ? <CheckIcon /> : <span className="w-1 h-1 rounded-full bg-current" />}
                </span>
                <div>
                  <p className="text-sm font-medium text-txt-primary">{PERMISSIONS_MAP[perm].label}</p>
                  <p className="text-xs text-txt-tertiary">{PERMISSIONS_MAP[perm].description}</p>
                </div>
              </div>
            )
          })}
        </div>
        <div className="px-6 py-4 border-t border-border flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium text-txt-secondary hover:bg-surface-hover border border-border transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
